/**
 * Inline bar chart, theme-aware and accessible — NO heavy library.
 *
 * Pure CSS render (per-bar height derived from the max); inherits theme tokens
 * (`bg-primary`). Accessible: the container is `role="img"` with an
 * `aria-label` that summarizes the series in text, and each bar exposes its
 * value in the `title` (native tooltip). No horizontal scroll: bars share the
 * width with `flex-1`.
 */
import { createMemo, For, Show } from "solid-js";
import { cn } from "@journal/shared";

const PERCENT_FULL = 100;
const CHART_HEIGHT_PX = 120;

export interface BarDatum {
  /** Short label under the bar (X axis). */
  label: string;
  /** Numeric value (Y axis). */
  value: number;
  /** Custom accessible/tooltip label (default `${label}: ${value}`). */
  title?: string;
  /** Highlights this bar (e.g. the current period). */
  highlight?: boolean;
  /** Click → interconnection (e.g. open that month/week). */
  onSelect?: () => void;
}

interface BarChartProps {
  data: () => BarDatum[];
  /** Accessible text summary of the whole series (role="img" aria-label). */
  summary: string;
  class?: string;
  /** Optional suffix for the value displayed above the bar. */
  heightPx?: number;
}

export function BarChart(props: BarChartProps) {
  const max = createMemo(() => Math.max(1, ...props.data().map((d) => d.value)));
  const height = () => props.heightPx ?? CHART_HEIGHT_PX;

  return (
    <Show
      when={props.data().length > 0}
      fallback={<p class="text-sm text-muted-foreground/70">{props.summary}</p>}
    >
      <div
        role="img"
        aria-label={props.summary}
        data-testid="bar-chart"
        class={cn("flex items-end gap-1.5", props.class)}
        style={{ height: `${height()}px` }}
      >
        <For each={props.data()}>
          {(d) => {
            const pct = () => (d.value / max()) * PERCENT_FULL;
            const inner = (
              <>
                <span class="text-[10px] tabular-nums text-muted-foreground/70" aria-hidden="true">
                  {d.value}
                </span>
                <div class="flex w-full flex-1 items-end">
                  <div
                    class={cn(
                      // GPU-only reveal: scaleY from the base (transform-origin:
                      // bottom) instead of animating `height` (layout/reflow).
                      "w-full rounded-t animate-bar-grow will-change-transform",
                      d.highlight ? "bg-primary" : "bg-primary/55",
                    )}
                    style={{
                      height: `${Math.max(pct(), d.value > 0 ? 4 : 0)}%`,
                      "min-height": d.value > 0 ? "3px" : "0",
                    }}
                  />
                </div>
                <span
                  class="w-full truncate text-center text-[9px] text-muted-foreground/55"
                  aria-hidden="true"
                >
                  {d.label}
                </span>
              </>
            );
            return (
              <Show
                when={d.onSelect}
                fallback={
                  <div
                    class="flex flex-1 flex-col items-center justify-end gap-1"
                    title={d.title ?? `${d.label}: ${d.value}`}
                    data-testid="bar-chart-bar"
                  >
                    {inner}
                  </div>
                }
              >
                <button
                  type="button"
                  class="flex flex-1 cursor-pointer flex-col items-center justify-end gap-1 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title={d.title ?? `${d.label}: ${d.value}`}
                  data-testid="bar-chart-bar"
                  onClick={() => d.onSelect?.()}
                >
                  {inner}
                </button>
              </Show>
            );
          }}
        </For>
      </div>
    </Show>
  );
}
